import {
  DealDetail,
  ClearingHouseInteraction,
  ClearingTradeType,
  TradingType,
  RepoType,
  CollateralBasketType,
  EvergreenNoticePeriod,
  Deal,
  CollateralType,
  WithDeal,
  WithCollateral
} from 'api'
import { isNil, orderBy } from 'lodash'
import { isToday, dateLikeCompareAscendingFn } from 'utils'

const COLUMN_HEADER_LINE =
  'sender.bic,sender.ref,prep.date,trade_date,settlement_d,prct,startamount,seller.lookupc,seller.referen,buyer.lookup,buyer.referen,nominal,isin,termda,rate,endam,isbund'

export const EMPTY_SECTION_CONTENT = 'No trades'

export const CASH_SECTION = ['CASH', COLUMN_HEADER_LINE, EMPTY_SECTION_CONTENT]

export const REPO_SECTION_HEADERS = ['REPO', COLUMN_HEADER_LINE]

export const DBV_SECTION_HEADERS = ['DBV', COLUMN_HEADER_LINE]

const createContingencyMessages: (
  dealDetails: DealDetail[],
  clearingTradeType: ClearingTradeType,
  clearingHouseInteractions: ClearingHouseInteraction[],
  clearingHouseId: string
) => string[] = (dealDetails, clearingTradeType, clearingHouseInteractions, clearingHouseId) => {
  const dealIdLookup = new Set(
    dealDetails
      .filter(dealDetail => dealDetail.clearingHouse !== null && dealDetail.clearingHouse.id === clearingHouseId)
      .map(dealDetail => dealDetail.deal.dealId)
  )

  if (dealIdLookup.size === 0) {
    return []
  }

  return clearingHouseInteractions
    .filter(
      clearingHouseInteraction =>
        clearingHouseInteraction.tradeType === clearingTradeType &&
        dealIdLookup.has(clearingHouseInteraction.dealId) &&
        clearingHouseInteraction.messageSentInstant !== null &&
        isToday(clearingHouseInteraction.messageSentInstant) &&
        clearingHouseInteraction.contingencyMessage !== null
    )
    .sort((interaction1, interaction2) => dateLikeCompareAscendingFn(interaction1.messageSentInstant!, interaction2.messageSentInstant!))
    .map(clearingHouseInteraction => clearingHouseInteraction.contingencyMessage!)
}

export const createContingencyFileContent: (
  dealDetails: DealDetail[],
  clearingHouseInteractions: ClearingHouseInteraction[],
  clearingHouseId: string
) => string = (dealDetails, clearingHouseInteractions, clearingHouseId) => {
  const repoContingencyMessages = createContingencyMessages(dealDetails, ClearingTradeType.REPO, clearingHouseInteractions, clearingHouseId)
  const dbvContingencyMessages = createContingencyMessages(dealDetails, ClearingTradeType.DBV, clearingHouseInteractions, clearingHouseId)
  const repoSection = [...REPO_SECTION_HEADERS, ...(repoContingencyMessages.length > 0 ? repoContingencyMessages : [EMPTY_SECTION_CONTENT])]
  const dbvSection = [...DBV_SECTION_HEADERS, ...(dbvContingencyMessages.length > 0 ? dbvContingencyMessages : [EMPTY_SECTION_CONTENT])]
  const lines = [...CASH_SECTION, ...repoSection, ...dbvSection]
  return lines.join('\n')
}

export const getTradingEntityAssignmentTradingType = ({ deal }: DealDetail) =>
  isNil(deal.notional) ? TradingType.CASH : TradingType.NOTIONAL

export const isDealDetailSBL = ({ deal }: DealDetail) => deal.repoType === RepoType.STOCK_BORROWING_LENDING

export const isDealDetailGcPlus = (dealDetail: DealDetail) => dealDetail.productSector?.basketType === CollateralBasketType.GC_PLUS

export const translateEvergreenNoticePeriodToNumber = (evergreenNoticePeriod: EvergreenNoticePeriod): number => {
  switch (evergreenNoticePeriod) {
    case EvergreenNoticePeriod.THIRTY:
      return 30
    case EvergreenNoticePeriod.SIXTY:
      return 60
    case EvergreenNoticePeriod.NINETY:
      return 90
    default:
      throw new Error(`Unhandled Evergreen Notice Period ${[evergreenNoticePeriod]}`)
  }
}

export function getDealInterestPeriodId(deal: Deal | null | undefined) {
  if (deal) {
    // deal interest period is one version less than a deal history item
    return `${deal.dealId}-${deal.version - 1}`
  }

  return ''
}

export function getDealIndexRatio<T extends WithDeal & WithCollateral>({ collateral, deal }: T): number | null {
  if (collateral?.collateralType !== CollateralType.INDEX_LINKED) {
    return null
  }

  return deal?.indexRatio ?? null
}

export function sortDealDetailsByLastUpdatedAt(dealDetails: DealDetail[]) {
  return orderBy(dealDetails, ['deal.lastUpdatedAt', 'deal.replacedWithDealId'], ['desc', 'desc'])
}
